import { ImageField } from '@sitecore-content-sdk/nextjs';

import { Data } from '@generated/Foundation.GMI.model';
import { SiteSettings } from 'lib/page-props-factory/plugins/site-settings';
import { RecipeResponse } from 'lib/services/models/content-response';

type BadgeSettingsItem = Data.Settings.BadgeSettings | Data.Settings.BadgeSettings_Item;

export type BadgeAwareSiteSettings = SiteSettings & {
  recipeDetailSiteSettings?: Data.Settings.RecipeDetailSiteSettings & {
    fields?: Data.Settings.RecipeDetailSiteSettings['fields'] & {
      badgeSettings?: BadgeSettingsItem;
    };
  };
};

export interface BadgeResolvedData {
  badgeLabel?: string;
  badgeLinkURL?: string;
  badgeLogo?: ImageField;
  badgeRibbon?: ImageField;
  badgeText?: string;
  contestName?: string | null;
  contestantFullName?: string | null;
  contestantLocation?: string;
  showMemberInfo: boolean;
}

type RuntimeBadgeSettings = BadgeSettingsItem | { fields?: BadgeSettingsItem['fields'] };

const normalizeGuid = (value?: string | null) => value?.replace(/[{}]/g, '').trim().toLowerCase();

const splitMetadataValue = (value?: string | null) =>
  (value || '')
    .split(/[|,\n\r]+/)
    .map((item) => normalizeGuid(item))
    .filter((item): item is string => Boolean(item));

const formatContestantLocation = (recipe: RecipeResponse) => {
  return [
    recipe.ContestantCityName?.trim(),
    recipe.ContestantStateName?.trim(),
    recipe.ContestantCountryName?.trim(),
  ]
    .filter(Boolean)
    .join(', ');
};

export const getBadgeSettings = (siteSettings?: BadgeAwareSiteSettings | null) => {
  if (!siteSettings) {
    return undefined;
  }

  return (
    siteSettings.badgeSettings ||
    (siteSettings as unknown as { badgeSettings?: RuntimeBadgeSettings }).badgeSettings ||
    (siteSettings.recipeDetailSiteSettings?.fields?.badgeSettings as
      | RuntimeBadgeSettings
      | undefined) ||
    (
      siteSettings.recipeDetailSiteSettings as unknown as {
        badgeSettings?: RuntimeBadgeSettings;
        fields?: { badgeSettings?: RuntimeBadgeSettings };
      }
    )?.badgeSettings ||
    (
      siteSettings.recipeDetailSiteSettings as unknown as {
        badgeSettings?: RuntimeBadgeSettings;
        fields?: { badgeSettings?: RuntimeBadgeSettings };
      }
    )?.fields?.badgeSettings
  );
};

const isPillsburySite = (siteSettings?: BadgeAwareSiteSettings | null, siteName?: string) => {
  const normalizedSiteName = siteName?.trim().toLowerCase();
  const normalizedSettingsSiteName = siteSettings?.siteName?.value?.trim().toLowerCase();
  const normalizedBrandStyle = siteSettings?.brandStyle?.value?.trim().toLowerCase();

  return (
    normalizedSiteName === 'pillsbury' ||
    normalizedSettingsSiteName === 'pillsbury' ||
    normalizedBrandStyle === 'brandspillsbury'
  );
};

export function extractBadgeData(
  recipe?: RecipeResponse,
  siteSettings?: BadgeAwareSiteSettings | null,
  siteName?: string
): BadgeResolvedData | null {
  const badgeSettings = getBadgeSettings(siteSettings);
  const badgeFields = badgeSettings?.fields;

  if (!recipe || !isPillsburySite(siteSettings, siteName) || !badgeFields?.showBadge?.value) {
    return null;
  }

  const badgeMetadataIds = splitMetadataValue(badgeFields.badgeMetadata?.value);
  const recipeMetadataIds = (recipe.MetadataIds || [])
    .map((item) => normalizeGuid(item))
    .filter((item): item is string => Boolean(item));

  if (
    !badgeMetadataIds.length ||
    !recipeMetadataIds.some((item) => badgeMetadataIds.includes(item))
  ) {
    return null;
  }

  const contestName = recipe.ContestName?.trim() || null;
  const contestantFullName = recipe.ContestantFullName?.trim() || null;
  const contestantLocation = formatContestantLocation(recipe);

  const hasRenderableContent =
    badgeFields.badgeRibbon?.value?.src ||
    badgeFields.badgeLogo?.value?.src ||
    badgeFields.badgeLabel?.value ||
    badgeFields.badgeText?.value ||
    contestName ||
    contestantFullName ||
    contestantLocation;

  if (!hasRenderableContent) {
    return null;
  }

  return {
    badgeLabel: badgeFields.badgeLabel?.value?.trim() || undefined,
    badgeLinkURL: badgeFields.badgeLinkURL?.value?.trim() || undefined,
    badgeLogo: badgeFields.badgeLogo,
    badgeRibbon: badgeFields.badgeRibbon,
    badgeText: badgeFields.badgeText?.value?.trim() || undefined,
    contestName,
    contestantFullName,
    contestantLocation: contestantLocation || undefined,
    showMemberInfo: Boolean(badgeFields.showMemberInfo?.value),
  };
}
